import React, { useEffect } from 'react';
import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { Title, TopicsSection } from 'view/styles/help-center';
import { AnalyticsContext, useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ReactPortal } from '@atlassian/help-center-common-component/react-portal';
import TopicsRoot from '@atlassian/help-center-common-component/topics-root';
import { getHelpCenterAri, getCloudId } from '@atlassian/help-center-common-util/meta';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import { ExperienceSuccess } from '@atlassian/ufo';

import { helpCenterTopicsProductExperience } from '../experiences';
import Topic from '../topic/topic';
import type { topicsQuery } from './__generated__/topicsQuery.graphql';
import messages from './messages';
import {
    TopicsList,
    ListItem,
    GreyWarningIcon,
    Message,
    ContactDescription,
    StyledHelpCenterContainer,
} from './styled';
interface Props {
    isDefaultViewLoading: boolean;
    shouldAlignWithHelpCenterContainer: boolean;
}

const Topics: FC<Props> = ({ isDefaultViewLoading, shouldAlignWithHelpCenterContainer }) => {
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { helpCenter } = useLazyLoadQuery<topicsQuery>(
        graphql`
            query topicsQuery($helpCenterAri: ID!, $cloudId: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: ["HelpCenterAggBeta"]) {
                        ... on HelpCenter {
                            topics {
                                topicId
                                properties
                                ... on HelpCenterTopic {
                                    ...topicFragment
                                }
                                items {
                                    edges {
                                        node {
                                            data {
                                                __typename
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
            }
        `,
        {
            helpCenterAri: getHelpCenterAri(),
            cloudId: getCloudId(),
        }
    );
    const topics = helpCenter?.helpCenterById?.topics || [];

    const visibleTopics = [
        ...topics.reduce<typeof topics>((acc, current) => {
            try {
                // Suppressing existing violation. Please fix this.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const properties = JSON.parse(current.properties);
                if (
                    // TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    properties?.hidden === 'true' ||
                    current.items?.edges?.every((o) => o?.node?.data?.__typename === 'HelpObjectStoreQueryError')
                ) {
                    return acc;
                }
            } catch (e) {
                reportError(e);
            }
            return acc.concat(current);
        }, []),
    ];

    useEffect(() => {
        if (visibleTopics.length > 0) {
            const event = createAnalyticsEvent({
                analyticsType: UI_EVENT_TYPE,
                action: 'viewed',
                topics_count: visibleTopics.length,
            });
            event.fire();
        }
    }, [createAnalyticsEvent, visibleTopics.length]);

    useEffect(() => {
        try {
            localStorage.setItem('topicsCount', visibleTopics.length.toString());
        } catch (e) {
            //
        }
    });

    useEffect(() => {
        if (helpCenter?.helpCenterById?.__typename === 'QueryError') {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            helpCenterTopicsProductExperience.failure();
        }
    }, [helpCenter]);

    if (visibleTopics.length === 0) {
        // Disabling existing violations, should be fixed when revisited.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        helpCenterTopicsProductExperience.abort();

        // Currently, we assume that no-portal-content will be added to page before topics are evaluated.
        // Needs to be fixed later if topics response start coming before portals
        return !isDefaultViewLoading && document.getElementById('no-portal-content') != null ? (
            <ReactPortal target={document.getElementById('no-portal-content') ?? undefined}>
                <GreyWarningIcon />
                <Message>
                    <FormattedMessage {...messages.allPortalsHidden} />
                </Message>
                <ContactDescription>
                    <FormattedMessage {...messages.allPortalsHiddenAction} />
                </ContactDescription>
            </ReactPortal>
        ) : null;
    }
    const Container =
        shouldAlignWithHelpCenterContainer && visibleTopics.length === 1 ? StyledHelpCenterContainer : React.Fragment;
    return (
        <Container>
            <TopicsRoot>
                <Title>{formatMessage(messages.topicsSectionLabel)}</Title>
                <TopicsSection count={visibleTopics.length}>
                    <TopicsList count={visibleTopics.length}>
                        {visibleTopics.map((topic, index) => (
                            <ListItem key={topic.topicId}>
                                <AnalyticsContext data={{ attributes: { topicPosition: index + 1 } }}>
                                    <Topic topic={topic} />
                                </AnalyticsContext>
                            </ListItem>
                        ))}
                    </TopicsList>
                </TopicsSection>
                <ExperienceSuccess experience={helpCenterTopicsProductExperience} />
            </TopicsRoot>
        </Container>
    );
};

export default withAnalyticsContext({
    component: 'topics',
})(Topics);
